import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { Container, Section } from '../../styles/GlobalStyles';

const CTAContainer = styled(Section)<{ color: string }>`
  background: ${props => props.color};
  padding: ${theme.spacing['4xl']} 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(0, 0, 0, 0.05) 100%);
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.xl} 0;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.lg} 0;
  }
`;

const CTAContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const CTATitle = styled.h2`
  color: ${theme.colors.neutral.white};
  margin-bottom: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    margin-bottom: ${theme.spacing.md};
  }
`;

const CTADescription = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.neutral.white};
  margin-bottom: ${theme.spacing['2xl']};
  line-height: ${theme.typography.lineHeight.relaxed};
  opacity: 0.95;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.base};
    margin-bottom: ${theme.spacing.lg};
  }
`;

const CTAButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTAButton = styled.button`
  background: ${theme.colors.neutral.white};
  color: ${theme.colors.primary.data};
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.normal};

  &:hover {
    transform: translateY(-2px);
    background: ${theme.colors.neutral.light};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    justify-content: center;
    font-size: ${theme.typography.fontSize.base};
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
  border: 2px solid ${theme.colors.neutral.white};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.neutral.white};
    color: ${theme.colors.primary.data};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    justify-content: center;
    font-size: ${theme.typography.fontSize.base};
  }
`;

interface CTASectionProps {
  title: string;
  description: string;
  color: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
}

const CTASection: React.FC<CTASectionProps> = ({
  title,
  description,
  color,
  primaryButtonText = "Fale Conosco",
  secondaryButtonText = "Ver Outras Soluções",
  onPrimaryClick,
  onSecondaryClick,
}) => {
  return (
    <CTAContainer color={color}>
      <Container>
        <CTAContent>
          <CTATitle>{title}</CTATitle>
          <CTADescription>{description}</CTADescription>
          <CTAButtonGroup>
            <CTAButton onClick={onPrimaryClick}>
              {primaryButtonText}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </CTAButton>
            {onSecondaryClick && (
              <SecondaryButton onClick={onSecondaryClick}>
                {secondaryButtonText}
              </SecondaryButton>
            )}
          </CTAButtonGroup>
        </CTAContent>
      </Container>
    </CTAContainer>
  );
};

export default CTASection;




